/**
 * Onboarding merchant list view with filtering and pagination
 */

import { FC, useEffect, useMemo, useState } from "react";
import {
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
} from "mantine-react-table";
import { useStyles } from "./Onboarding.style";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../store";
import { OnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { Text } from "@mantine/core";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { OnboardingData, ApiResponse } from "./types";
import { createOnboardingColumns } from "./tableConfig";
import { transformColumnFilters } from "./utils/onboardingUtils";
import {
  ROUTES,
  MESSAGES,
  TABLE_STYLES,
  DEFAULT_PAGINATION,
} from "./utils/onboardingConstants";

const useGetOnboardingData = (
  token: string | null,
  pagination: MRT_PaginationState,
  columnFilters: string,
) => {
  const [data, setData] = useState<OnboardingData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalRowCount, setTotalRowCount] = useState(0);

  useEffect(() => {
    if (!token) {
      setError(new Error("No authentication token provided"));
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/users/get-onboarding-status-user?page=${
            pagination.pageIndex + 1
          }&limit=${pagination.pageSize}${
            columnFilters ? `&filters=${columnFilters}` : ""
          }`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const result: ApiResponse = await response.json();
        const mappedData: OnboardingData[] = result.data.map((item) => ({
          merchantName: item.name,
          merchantEmail: item.email,
          merchantId: item.userId,
          onboardingStatus: item.status as OnboardingStatusEnum,
        }));
        setData(mappedData);
        setTotalRowCount(result.total);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("An unknown error occurred"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, pagination, columnFilters]);

  return { data, isLoading, error, totalRowCount };
};

const Onboarding: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] =
    useState<MRT_PaginationState>(DEFAULT_PAGINATION);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );

  const apiColumnFilters = transformColumnFilters(columnFilters);

  const { data, error, isLoading, totalRowCount } = useGetOnboardingData(
    token,
    pagination,
    apiColumnFilters,
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const handleMerchantClick = (
    status: OnboardingStatusEnum,
    merchantName: string,
    merchantEmail: string,
    merchantId: number,
  ) => {
    navigate(ROUTES.ONBOARDING_FORM, {
      state: {
        onboardingStatus: status,
        merchantName,
        merchantEmail,
        merchantId,
      },
    });
  };

  const columns = useMemo(
    () =>
      createOnboardingColumns({
        buttonClassName: classes.button,
        onMerchantClick: handleMerchantClick,
      }),
    [classes.button],
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: true,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, columnFilters },
    columns,
    data: data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    rowCount: totalRowCount,
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: TABLE_STYLES.headCell,
    },
    mantineTableBodyCellProps: {
      style: TABLE_STYLES.bodyCell,
    },
    renderEmptyRowsFallback: () => (
      <Text align="center" size="lg" color="dimmed">
        {MESSAGES.NO_DATA}
      </Text>
    ),
  });

  return (
    <div className={classes.onboardingContainer}>
      <Text className={classes.heading}>Onboarding Merchant</Text>
      <MantineReactTable table={table} />
    </div>
  );
};

export default Onboarding;
